import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useDelayedComponents } from "@hooks/useDelayedComponents";
import AppBanner from "@routes/Article/components/AppBanner";
import PodcastsFeaturedStories from "@routes/Podcasts/PodcastsPage/components/PodcastsFeaturedStories";
import PodcastsKeywordsSectionStories from "@routes/Podcasts/PodcastsPage/components/PodcastsKeywordsSectionStories/PodcastsSectionStories";
import PodcastsPromoteBannerVersion1 from "@routes/Podcasts/PodcastsPage/components/PodcastsPromoteBannerVersion1";
import PodcastsPromoteBannerVersion2 from "@routes/Podcasts/PodcastsPage/components/PodcastsPromoteBannerVersion2";
import {
  PodcastKeywordArticleMapping,
  PodcastsPageData,
} from "@routes/Podcasts/PodcastsPage/utils/types";
import {
  BT_CORRESPONDENTS_DETAILS,
  BT_PODCASTS_DETAILS,
  LENS_ON_DETAILS,
  MARKET_FOCUS_DETAILS,
  MONEY_HACKS_DETAILS,
} from "@routes/Podcasts/utils/constants";
import SectionHeading from "@routes/Section/components/SectionHeading";
import { cn } from "@util/helpers";

/**
 * Component to render the content of the Podcasts page.
 * Figma: https://www.figma.com/design/xsbEGttNPBTNUi388qtSJD/PODCASTS--WEB-?node-id=3702-361227&t=frjsk7DkSOdPUpFL-4
 */
const PodcastPageContent = ({
  podcastsFeaturedArticles,
  lensOnArticles,
  moneyHacksArticles,
  marketFocusArticles,
  btCorrespondentsArticles,
  btPodcastsArticles,
  seoFooterData,
}: PodcastsPageData): React.ReactElement => {
  const { isShown } = useDelayedComponents(800);

  const articleMapping: PodcastKeywordArticleMapping[] = [
    {
      name: "money_hacks",
      articles: moneyHacksArticles,
      details: MONEY_HACKS_DETAILS,
    },
    { name: "lens_on", articles: lensOnArticles, details: LENS_ON_DETAILS },
    {
      name: "bt_podcasts",
      articles: btPodcastsArticles,
      details: BT_PODCASTS_DETAILS,
    },
    {
      name: "bt_correspondents",
      articles: btCorrespondentsArticles,
      details: BT_CORRESPONDENTS_DETAILS,
    },
    {
      name: "market_focus",
      articles: marketFocusArticles,
      details: MARKET_FOCUS_DETAILS,
    },
  ];

  return (
    <div className="w-full" data-testid="podcasts-page-content-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container rootClassName="grid grid-cols-1 lg:grid-cols-3">
        <SectionHeading
          text={"Podcasts"}
          className={"cols-span-1 uppercase lg:col-span-3"}
          hasBorder
        />

        <div className="col-span-1 lg:col-span-2 lg:mb-6 lg:mr-6">
          <PodcastsFeaturedStories
            className="cols-span-2 pt-2"
            cardProps={podcastsFeaturedArticles}
          />
        </div>

        <div className="mt-6 h-full lg:row-span-2">
          <PodcastsPromoteBannerVersion1 />

          <Ad
            adType="imu1"
            className="mb-10 flex flex-col items-center justify-center self-start bg-gray-150 py-4 lg:sticky lg:lg:top-[70px] lg:pt-6"
          />
        </div>

        <div className="col-span-1 lg:col-span-2 lg:mr-6">
          {articleMapping.map((mapping, index) => {
            return (
              <>
                <PodcastsKeywordsSectionStories
                  cardProps={mapping.articles}
                  details={mapping.details}
                  key={mapping.name}
                  className={cn({ "mb-0": index === 4 || index === 1 })}
                />

                {index === 1 || index === 4 ? (
                  <PodcastsPromoteBannerVersion2 />
                ) : null}
              </>
            );
          })}
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      {isShown ? <AppBanner /> : null}

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default PodcastPageContent;
